import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';

/**
 * The small "…" menu on a table row (ledger row 132).
 *
 * The menu is drawn into document.body at the button's position. The tables
 * scroll sideways inside their own box on a phone, and a box that scrolls one
 * way clips the other, so a menu drawn inside the row would be cut off or hidden.
 * It closes on a click elsewhere, Escape, or any scroll (which would leave it
 * floating away from its row).
 *
 * items: [{ label, icon, onSelect, danger }]
 */
const RowMenu = ({ label, items }) => {
    const [position, setPosition] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const open = position !== null;

    const toggle = () => {
        if (open) {
            setPosition(null);
            return;
        }
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
    };

    useEffect(() => {
        if (!open) return undefined;
        // The menu sits at the end of the page, so keyboard focus is moved into
        // it; otherwise Tab would carry on down the table and never reach it.
        menuRef.current?.querySelector('[role="menuitem"]')?.focus({ preventScroll: true });
        const close = () => setPosition(null);
        const onPointer = (event) => {
            if (menuRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return;
            close();
        };
        const onKey = (event) => {
            if (event.key === 'Escape') {
                close();
                buttonRef.current?.focus();
                return;
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            const entries = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
            if (!entries.length) return;
            event.preventDefault();
            const at = entries.indexOf(document.activeElement);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            entries[(at + step + entries.length) % entries.length].focus({ preventScroll: true });
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="pp-icon-btn"
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={toggle}
            >
                <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
            {open && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="pp-row-menu"
                    role="menu"
                    aria-label={label}
                    style={{ top: position.top, right: position.right }}
                >
                    {items.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            role="menuitem"
                            className={`pp-row-menu-item ${item.danger ? 'pp-row-menu-item--danger' : ''}`}
                            onClick={() => {
                                setPosition(null);
                                item.onSelect();
                            }}
                        >
                            {item.icon ? <FontAwesomeIcon icon={item.icon} /> : null}
                            {item.label}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </>
    );
};

export default RowMenu;
